import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle, PageHeader } from '@/components/ui'
import { ExternalLink, Copy } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/lib/auth-store'

export const Route = createFileRoute('/_app/docs')({
  component: DocsPage,
})

function DocsPage() {
  const { apiUrl } = useAuth()

  return (
    <>
      <PageHeader
        title="Documentación"
        description="Cómo onboardear nuevos tenants y consumir el API"
      />
      <div className="p-8 space-y-6 max-w-4xl">
        <Card>
          <CardHeader><CardTitle>1. Crear un nuevo tenant</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">POST con admin token:</p>
            <Code>{`curl -X POST ${apiUrl}/api/admin/tenants \\
  -H "Authorization: Bearer <ADMIN_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "slug": "marca-b",
    "name": "Marca B Store",
    "primaryHostname": "marca-b.com",
    "defaultCurrency": "COP",
    "defaultLocale": "es"
  }'`}</Code>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>2. Generar API key del tenant</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">Esta key se usa en el storefront del tenant:</p>
            <Code>{`curl -X POST ${apiUrl}/api/admin/api-keys \\
  -H "Authorization: Bearer <ADMIN_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tenantId": "<TENANT_UUID>",
    "name": "Production storefront",
    "environment": "live",
    "scopes": ["*"]
  }'`}</Code>
            <p className="text-xs text-muted-foreground mt-2">
              ⚠️ El campo <code>fullKey</code> de la respuesta solo se muestra UNA vez. Cópialo.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>3. Consumir desde el storefront</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">Con la API key generada, listar productos:</p>
            <Code>{`curl ${apiUrl}/api/v1/products \\
  -H "Authorization: Bearer sk_live_xxxxxxxxxxxx"`}</Code>
            <p className="text-sm text-muted-foreground mt-4 mb-2">O via SDK tipado:</p>
            <Code>{`import { createCoreClient } from '@core/sdk'

const core = createCoreClient({
  baseUrl: '${apiUrl}',
  apiKey: 'sk_live_xxxxxxxxxxxx',
})

const { data, nextCursor } = await core.products.list({
  limit: 20,
  status: 'published',
})`}</Code>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Endpoints disponibles</CardTitle></CardHeader>
          <CardContent>
            <Endpoint method="GET" path="/healthz" auth="—" />
            <Endpoint method="GET" path="/api/admin/dashboard" auth="admin" />
            <Endpoint method="GET" path="/api/admin/tenants-rich" auth="admin" />
            <Endpoint method="GET" path="/api/admin/tenants" auth="admin" />
            <Endpoint method="POST" path="/api/admin/tenants" auth="admin" />
            <Endpoint method="GET" path="/api/admin/tenants/:id" auth="admin" />
            <Endpoint method="GET" path="/api/admin/tenants/:id/stats" auth="admin" />
            <Endpoint method="GET" path="/api/admin/tenants/:id/usage" auth="admin" />
            <Endpoint method="POST" path="/api/admin/tenants/:id/suspend" auth="admin" />
            <Endpoint method="POST" path="/api/admin/tenants/:id/reactivate" auth="admin" />
            <Endpoint method="GET" path="/api/admin/api-keys/tenant/:tenantId" auth="admin" />
            <Endpoint method="POST" path="/api/admin/api-keys" auth="admin" />
            <Endpoint method="DELETE" path="/api/admin/api-keys/:id" auth="admin" />
            <Endpoint method="GET" path="/api/v1/products" auth="tenant API key" />
            <Endpoint method="POST" path="/api/v1/products" auth="tenant API key" />
            <Endpoint method="GET" path="/api/v1/products/:id" auth="tenant API key" />
            <Endpoint method="PATCH" path="/api/v1/products/:id" auth="tenant API key" />
            <Endpoint method="DELETE" path="/api/v1/products/:id" auth="tenant API key" />
          </CardContent>
        </Card>

        <Card className="bg-[hsl(var(--surface))]">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              📚 Documentación completa próximamente en <code>docs.headless-commerce.dev</code>
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function Code({ children }: { children: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="relative group">
      <pre className="bg-background border border-border rounded p-4 text-xs font-mono overflow-x-auto text-foreground">
        {children}
      </pre>
      <button
        onClick={() => { navigator.clipboard.writeText(children); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
        className="absolute top-2 right-2 p-1.5 rounded bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted))] opacity-0 group-hover:opacity-100 transition"
      >
        <Copy className="size-3" />
      </button>
      {copied && <span className="absolute top-2 right-10 text-xs text-green-700 dark:text-green-400">Copiado</span>}
    </div>
  )
}

function Endpoint({ method, path, auth }: { method: string; path: string; auth: string }) {
  const colors: Record<string, string> = {
    GET: 'text-green-700 dark:text-green-400',
    POST: 'text-blue-700 dark:text-blue-400',
    PATCH: 'text-yellow-700 dark:text-yellow-400',
    DELETE: 'text-red-700 dark:text-red-400',
  }
  return (
    <div className="flex items-center gap-3 py-1.5 text-xs font-mono border-b border-border last:border-0">
      <span className={`w-12 text-right font-bold ${colors[method]}`}>{method}</span>
      <span className="flex-1 text-foreground">{path}</span>
      <span className="text-muted-foreground">{auth}</span>
    </div>
  )
}
